import { useMemo } from 'react';
import { LwdFeatureSeats } from '@/api';
import { useDevices } from '../providers';
import { useCompanyPermissions } from '@/companies/hooks';
import { useFetchSubscriptionUsage } from '@/companies/queries';
import {
    isFallEffectivelyEnabled,
    isTiltEffectivelyEnabled
} from '../util/device-alert-usage';
import { deriveDeviceFeatureStates } from '../util/device-feature-states';

type BulkAlertLicenseAvailability = {
    /** The total number of addon licences. */
    limit: number;
    /**
     * Licences available for newly enabling devices in the selection: the limit
     * minus licences already held by devices that will keep consuming one after
     * the bulk apply (devices outside the selection that are enabled, plus
     * selected devices that are already enabled).
     */
    free: number;
    /** Selected devices not already using the feature (would each consume a licence). */
    toEnable: number;
};

/**
 * Licence availability for a bulk enable across `deviceIds`. Seat totals come
 * from the authoritative server-side usage endpoint: every currently-enabled
 * device keeps its seat after a bulk apply, so the server's `available` is
 * exactly what's `free` for newly enabling devices. `toEnable` is how many of
 * the *selected* devices (always in memory for the acting user) would newly
 * consume a licence. Enabling is allowed when `toEnable <= free`. `loading` is
 * true until usage has resolved.
 */
export function useBulkAlertLicenses(params: {
    companyId: string;
    deviceIds: string[];
}): {
    fall: BulkAlertLicenseAvailability;
    tilt: BulkAlertLicenseAvailability;
    geo: BulkAlertLicenseAvailability;
    loading: boolean;
} {
    const { companyId, deviceIds } = params;
    const { devices } = useDevices(companyId);
    const { canEditAnyDevice } = useCompanyPermissions();
    const { usage, status } = useFetchSubscriptionUsage(companyId, {
        enabled: canEditAnyDevice
    });

    return useMemo(() => {
        const loading = status !== 'success';
        const selected = new Set(deviceIds);
        const selectedDevices = devices.filter((d) => selected.has(d.id));

        // How many of the selected devices would newly consume a licence (those
        // not already effectively enabled, pending changes included).
        let fallToEnable = 0;
        let tiltToEnable = 0;
        let geoToEnable = 0;
        selectedDevices.forEach((device) => {
            if (!isFallEffectivelyEnabled(device)) fallToEnable++;
            if (!isTiltEffectivelyEnabled(device)) tiltToEnable++;
            if (!deriveDeviceFeatureStates(device).geofence.enabled) {
                geoToEnable++;
            }
        });

        function availability(
            seats: LwdFeatureSeats | undefined,
            toEnable: number
        ): BulkAlertLicenseAvailability {
            return {
                limit: seats?.total ?? 0,
                free: seats?.available ?? 0,
                toEnable
            };
        }

        return {
            fall: availability(usage?.lwd?.fall, fallToEnable),
            tilt: availability(usage?.lwd?.tilt, tiltToEnable),
            geo: availability(usage?.lwd?.geofence, geoToEnable),
            loading
        };
    }, [devices, deviceIds, usage, status]);
}
